import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { tokenIsExpired } from "common";
import { get } from "lodash";
import moment from "moment";
import { login, LoginForm, logout } from "./identityAPI";
import {
  passwordRecovery,
  PasswordRecoveryForm,
} from "./password-recovery-request/stages/password-recovery/passwordRecoveryAPI";
import {
  passwordRecoveryRequest,
  PasswordRecoveryRequestForm,
} from "./password-recovery-request/stages/request/passwordRecoveryRequestAPI";
import {
  register,
  RegisterForm,
} from "./register/stages/registerForm/registerAPI";

export interface IdentityData {
  access_token: string;
  expires_in: number;
  expires_at?: number;
  success?: boolean;
  token_type: string;
}

export interface IdentityState {
  data: IdentityData;
  status: "idle" | "loading" | "failed";
}

const initialState: IdentityState = {
  data: {} as IdentityData,
  status: "idle",
};

export const loginAsync = createAsyncThunk(
  "identity/login",
  async (item: LoginForm, { rejectWithValue }) => {
    const { data, error, success } = await login(item);
    if (success) {
      // The API wraps the token payload as {success, data: {access_token, ...}, message}
      return data?.data || data;
    }
    return rejectWithValue(error);
  },
);

export const logoutAsync = createAsyncThunk(
  "identity/logout",
  async (_: void, { getState }) => {
    //On expired token we just should return true as resolce thunk
    const state = getState();
    const access_token = get(state, "identity.data.access_token");
    const expires_at = get(state, "identity.data.expires_at", 0);
    const expired = access_token && tokenIsExpired(expires_at);
    if (expired) {
      return true;
    }
    await logout();
    return true;
  },
);

export const passwordRecoveryRequestAsync = createAsyncThunk(
  "identity/password-recovery-request",
  async (item: PasswordRecoveryRequestForm, { rejectWithValue }) => {
    const { data, error, success } = await passwordRecoveryRequest(item);
    if (success) {
      return data;
    }
    return rejectWithValue(error);
  },
);

export const passwordRecoveryAsync = createAsyncThunk(
  "identity/password-recovery",
  async (
    { item, token }: { item: PasswordRecoveryForm; token: string },
    { rejectWithValue },
  ) => {
    const { data, error, success } = await passwordRecovery(item, token);
    if (success) {
      return data;
    }
    return rejectWithValue(error);
  },
);

export const registerAsync = createAsyncThunk(
  "identity/register",
  async ({ item }: { item: RegisterForm }, { rejectWithValue }) => {
    const { data, error, success } = await register(item);
    if (success) {
      return data;
    }
    return rejectWithValue(error);
  },
);

export const refreshAsync = createAsyncThunk(
  "identity/refresh",
  async (_: void, { dispatch, rejectWithValue }) => {
    dispatch({ type: "identity/logout/fulfilled" });
    return rejectWithValue(false);
  },
  {
    condition: (_: void, { getState }): boolean | undefined => {
      //only fire when the stored token has already expired, to trigger the logout above
      const state = getState();
      const access_token = get(state, "identity.data.access_token");
      const expires_at = get(state, "identity.data.expires_at", 0);
      return !!access_token && tokenIsExpired(expires_at);
    },
  },
);

export const identitySlice = createSlice({
  name: "identity",
  initialState,
  reducers: {
    resetStatus: state => {
      state.status = "idle";
    },
    logoutSync: state => {
      state.status = "idle";
      state.data = {} as IdentityData;
    },
  },
  extraReducers: builder => {
    //Login
    builder
      .addCase(loginAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.data = action.payload;
        state.data.expires_at = moment().unix() + action.payload.expires_in;
      })
      .addCase(loginAsync.rejected, state => {
        state.status = "idle";
      });

    //Expired-token check (no refresh flow with JWT tokens)
    builder.addCase(refreshAsync.rejected, state => {
      state.status = "idle";
    });

    //Logout
    builder
      .addCase(logoutAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(logoutAsync.fulfilled, state => {
        state.status = "idle";
        state.data = {} as IdentityData;
      })
      .addCase(logoutAsync.rejected, state => {
        state.status = "idle";
        state.data = {} as IdentityData;
      });

    // Password recovery request
    builder
      .addCase(passwordRecoveryRequestAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(passwordRecoveryRequestAsync.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(passwordRecoveryRequestAsync.rejected, state => {
        state.status = "idle";
      });

    // Password recovery
    builder
      .addCase(passwordRecoveryAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(passwordRecoveryAsync.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(passwordRecoveryAsync.rejected, state => {
        state.status = "idle";
      });

    // Register
    builder
      .addCase(registerAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(registerAsync.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(registerAsync.rejected, state => {
        state.status = "idle";
      });
  },
});

export const { resetStatus, logoutSync } = identitySlice.actions;

export const selectIdentity = (state: RootState) => state.identity.data;
export const selectIdentityStatus = (state: RootState) => state.identity.status;
export const selectAccessToken = (state: RootState) =>
  state.identity.data.access_token;
export const selectAccessTokenExpiresAt = (state: RootState) =>
  state.identity.data.expires_at;

export default identitySlice.reducer;
